import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Loader2, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMAGE =
  "https://images.pexels.com/photos/16176576/pexels-photo-16176576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password, rememberMe);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || "Login failed");
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-slate-50">
      {/* Left visual */}
      <div
        className="hidden lg:block lg:col-span-3 relative"
        data-testid="login-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 border border-white/40 flex items-center justify-center">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <div className="label-eyebrow text-white/80">KGOF Dealership</div>
              <div className="font-black text-lg tracking-tight">To Do — Ops Console</div>
            </div>
          </div>
          <div className="max-w-lg">
            <div className="label-eyebrow text-white/70 mb-3">Team Operations</div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-tight">
              Every task, every technician,<br />under one roof.
            </h1>
            <p className="mt-6 text-white/80 text-base leading-relaxed max-w-md">
              Assign, track, and close out daily service jobs across your dealership floor —
              from sales prep to bay handoffs.
            </p>
          </div>
          <div className="text-xs text-white/50">© {new Date().getFullYear()} KGOF Automotive Group</div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center px-6 py-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 border border-slate-300 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-slate-800" />
            </div>
            <div>
              <div className="label-eyebrow">KGOF Dealership</div>
              <div className="font-black text-slate-900">To Do — Ops Console</div>
            </div>
          </div>

          <div className="label-eyebrow">Sign in</div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Access your workspace
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Use your dealership-issued credentials to continue.
          </p>

          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@kfog.co.in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-eyebrow">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-1" data-testid="login-remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 accent-slate-900 cursor-pointer"
                data-testid="login-remember-me"
              />
              <span className="text-sm text-slate-600">
                Remember me <span className="text-slate-400">(stay signed in for 30 days)</span>
              </span>
            </label>

            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 rounded-md font-semibold transition-transform hover:-translate-y-[1px] active:translate-y-[1px]"
              data-testid="login-submit-button"
            >
              {busy ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in…</>
              ) : (
                <><LogIn className="h-4 w-4 mr-2" />Sign in</>
              )}
            </Button>

            <div className="text-xs text-slate-500 pt-4 border-t border-slate-100">
              Trouble signing in? Contact your administrator.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
